import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from '@mui/material';
import {
  Pets as PetsIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { LostPetStats, LostPet } from '../types';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';

interface LostPetsWidgetProps {
  isVisible?: boolean;
}

const LostPetsWidget: React.FC<LostPetsWidgetProps> = ({ isVisible = true }) => {
  const [stats, setStats] = useState<LostPetStats | null>(null);
  const [recentLostPets, setRecentLostPets] = useState<LostPet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState<LostPet | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch stats first, then handle pets separately
      let statsData = null;
      let petsData = null;
      
      try {
        statsData = await adminApi.getLostPetStats();
        console.log('Lost pets stats API response:', statsData);
      } catch (statsErr) {
        console.warn('Lost pets stats API not available:', statsErr);
        // Use mock data if API is not available
        statsData = {
          total: 0,
          active: 0,
          found: 0,
          cancelled: 0,
          waitingApproval: 0,
          approved: 0,
          rejected: 0,
        };
      }
      
      try {
        petsData = await adminApi.getLostPets(1, 5, 'approved', 'active');
        console.log('Lost pets list API response:', petsData);
      } catch (petsErr) {
        console.warn('Lost pets list API not available:', petsErr);
        petsData = { 
          users: [], 
          pagination: {
            total: 0,
            totalPages: 0,
            currentPage: 1,
            limit: 5
          }
        };
      }
      
      setStats(statsData);
      // Handle different API response structures
      const pets = petsData?.users || [];
      setRecentLostPets(Array.isArray(pets) ? pets : []);
    } catch (err: any) {
      console.error('Lost pets fetch error:', err);
      setError('Kayıp pet verileri yüklenirken hata oluştu');
      setRecentLostPets([]);
      // Set default stats if everything fails
      setStats({
        total: 0,
        active: 0,
        found: 0,
        cancelled: 0,
        waitingApproval: 0,
        approved: 0,
        rejected: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchData, {
    refreshInterval: 60000, // 60 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const handleCardClick = () => {
    setDetailDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedPet(null);
  };

  const handlePetClick = (pet: LostPet) => {
    setSelectedPet(pet);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'warning';
      case 'found':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <SearchIcon />;
      case 'found':
        return <CheckCircleIcon />;
      case 'cancelled':
        return <CancelIcon />;
      default:
        return <PendingIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: 280, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Box sx={{ height: 280, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error || 'Veri yüklenemedi'}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box 
        sx={{ 
          height: 280,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          p: 3,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }
        }}
        onClick={handleCardClick}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <PetsIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Kayıp Petler
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {format(lastRefresh, 'HH:mm')}
            </Typography>
            <Tooltip title="Yenile">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); manualRefresh(); }} sx={{ p: 0.5 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" gap={3} mb={2}>
          <Box flex={1} textAlign="center">
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {stats?.total || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Toplam
            </Typography>
          </Box>
          <Box flex={1} textAlign="center">
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {stats?.active || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Aktif
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip 
            label={`${stats?.found || 0} Bulunan`} 
            size="small" 
            color="success" 
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: '0.75rem' }}
          />
          <Chip 
            label={`${stats?.waitingApproval || 0} Onay Bekleyen`} 
            size="small" 
            color="info" 
            icon={<PendingIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>

        {recentLostPets && recentLostPets.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
              Son Aktif Petler:
            </Typography>
            <Box sx={{ maxHeight: 80, overflow: 'auto' }}>
              {recentLostPets.slice(0, 3).map((pet) => (
                <Box 
                  key={pet._id} 
                  display="flex" 
                  alignItems="center" 
                  gap={1} 
                  py={0.5}
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); handlePetClick(pet); }}
                >
                  <Avatar 
                    src={pet.photos?.[0]} 
                    sx={{ width: 20, height: 20 }}
                  >
                    <PetsIcon sx={{ fontSize: 12 }} />
                  </Avatar>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.875rem' }}>
                    {pet.name}
                  </Typography>
                  <Chip 
                    label={pet.lostPetStatus} 
                    size="small" 
                    color={getStatusColor(pet.lostPetStatus) as any}
                    icon={getStatusIcon(pet.lostPetStatus)}
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PetsIcon color="primary" />
              <Typography variant="h6">Kayıp Pet Detayları</Typography>
            </Box>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
            <Box flex={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    İstatistikler
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Box flex={1} textAlign="center">
                      <Typography variant="h4" color="primary">
                        {stats?.total || 0}
                      </Typography>
                      <Typography variant="body2">Toplam</Typography>
                    </Box>
                    <Box flex={1} textAlign="center">
                      <Typography variant="h4" color="warning.main">
                        {stats?.active || 0}
                      </Typography>
                      <Typography variant="body2">Aktif</Typography>
                    </Box>
                    <Box flex={1} textAlign="center">
                      <Typography variant="h4" color="success.main">
                        {stats?.found || 0}
                      </Typography>
                      <Typography variant="body2">Bulunan</Typography>
                    </Box>
                    <Box flex={1} textAlign="center">
                      <Typography variant="h4" color="info.main">
                        {stats?.waitingApproval || 0}
                      </Typography>
                      <Typography variant="body2">Onay Bekleyen</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box flex={1}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Son Aktif Petler
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pet</TableCell>
                          <TableCell>Konum</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Tarih</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentLostPets && recentLostPets.map((pet) => (
                          <TableRow 
                            key={pet._id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handlePetClick(pet)}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar 
                                  src={pet.photos?.[0]} 
                                  sx={{ width: 32, height: 32 }}
                                >
                                  <PetsIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {pet.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {pet.breed}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <LocationIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {pet.lastSeenLocation.city}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={pet.lostPetStatus} 
                                size="small" 
                                color={getStatusColor(pet.lostPetStatus) as any}
                                icon={getStatusIcon(pet.lostPetStatus)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CalendarIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {format(new Date(pet.lostDate), 'dd.MM.yyyy')}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Pet Detail Dialog */}
      <Dialog 
        open={!!selectedPet} 
        onClose={() => setSelectedPet(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedPet && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar 
                    src={selectedPet.photos?.[0]} 
                    sx={{ width: 40, height: 40 }}
                  >
                    <PetsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedPet.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedPet.breed} • {selectedPet.type}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setSelectedPet(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" gutterBottom>
                      Pet Bilgileri
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Typography variant="body2">
                        <strong>Yaş:</strong> {selectedPet.age || 'Bilinmiyor'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Cinsiyet:</strong> {selectedPet.gender || 'Bilinmiyor'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Mikroçip:</strong> {selectedPet.microchipId || 'Yok'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle1" gutterBottom>
                      Konum Bilgileri
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Typography variant="body2">
                        <strong>Şehir:</strong> {selectedPet.lastSeenLocation.city}
                      </Typography>
                      {selectedPet.lastSeenLocation.district && (
                        <Typography variant="body2">
                          <strong>İlçe:</strong> {selectedPet.lastSeenLocation.district}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Açıklama:</strong> {selectedPet.lastSeenLocation.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Açıklama
                  </Typography>
                  <Typography variant="body2">
                    {selectedPet.description}
                  </Typography>
                </Box>
                {selectedPet.reward && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Ödül Bilgileri
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <MoneyIcon color="primary" />
                      <Typography variant="body2">
                        {selectedPet.reward.amount} {selectedPet.reward.currency}
                      </Typography>
                      {selectedPet.reward.description && (
                        <Typography variant="body2" color="text.secondary">
                          - {selectedPet.reward.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Sahip Bilgileri
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ad:</strong> {selectedPet.owner.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>E-posta:</strong> {selectedPet.owner.email}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedPet(null)}>Kapat</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default LostPetsWidget;
